import { Controller } from "@hotwired/stimulus"

// Karat purity factors (fraction of pure gold)
const KARAT_PURITY = {
  "24K": 24 / 24,
  "22K": 22 / 24,
  "18K": 18 / 24,
  "14K": 14 / 24,
  "10K": 10 / 24
}

const TROY_OZ_TO_GRAM = 31.1035
const TROY_OZ_TO_DWT = 20
const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

export default class extends Controller {
  static values = { initialPrices: Object }

  static targets = [
    "goldOzPrice", "silverOzPrice", "platinumOzPrice",
    "goldGramPrice", "silverGramPrice", "platinumGramPrice",
    "goldDwtPrice", "silverDwtPrice", "platinumDwtPrice",
    "weightInput", "weightUnit",
    "goldResults", "silverResult", "platinumResult",
    "updatedAt", "statusBadge", "marketTimestamp"
  ]

  connect() {
    // Server embeds cached prices in the page; use them for instant first paint
    // and only hit the JSON endpoint when the cache was empty at render time
    const initial = this.initialPricesValue
    if (initial && initial.prices) {
      this.applyPrices(initial.prices, initial.fallback, initial.updated_at, initial.stale)
    } else {
      this.fetchPrices()
    }
  }

  // Fetch live prices from backend
  async fetchPrices() {
    try {
      this.updateStatus("loading")
      const response = await fetch("/precious-metal-calculator/prices.json")
      const data = await response.json()

      if (data.prices) {
        this.applyPrices(data.prices, data.fallback, data.updated_at, data.stale)
      }
    } catch (error) {
      console.warn("Failed to fetch metal prices:", error)
      this.updateStatus("offline")
    }
  }

  applyPrices(prices, isFallback, updatedAt, isStale = false) {
    if (prices.XAU) {
      this.goldOzPriceTarget.value = parseFloat(prices.XAU.price_oz).toFixed(2)
    }
    if (prices.XAG) {
      this.silverOzPriceTarget.value = parseFloat(prices.XAG.price_oz).toFixed(2)
    }
    if (prices.XPT) {
      this.platinumOzPriceTarget.value = parseFloat(prices.XPT.price_oz).toFixed(2)
    }

    this.updateGramDisplays()
    this.updateStatus(isFallback ? "fallback" : isStale ? "stale" : "live")
    this.showFeedTimestamp(updatedAt)
    this.calculate()
  }

  // Show when the price feed itself was updated, not when the page fetched it
  showFeedTimestamp(updatedAt) {
    if (!this.hasUpdatedAtTarget || !updatedAt) return

    const time = new Date(updatedAt)
    if (!isNaN(time)) {
      this.updatedAtTarget.textContent = time.toLocaleString(undefined, { timeZoneName: "short" })
    }
  }

  // Called when user changes any market price input
  marketPriceChanged() {
    this.updateStatus("custom")
    this.updateGramDisplays()
    this.calculate()
  }

  // Update the gram and pennyweight price displays from oz inputs
  updateGramDisplays() {
    const goldOz = parseFloat(this.goldOzPriceTarget.value) || 0
    const silverOz = parseFloat(this.silverOzPriceTarget.value) || 0
    const platinumOz = parseFloat(this.platinumOzPriceTarget.value) || 0

    this.goldGramPriceTarget.textContent = USD_FORMATTER.format(goldOz / TROY_OZ_TO_GRAM)
    this.silverGramPriceTarget.textContent = USD_FORMATTER.format(silverOz / TROY_OZ_TO_GRAM)
    this.platinumGramPriceTarget.textContent = USD_FORMATTER.format(platinumOz / TROY_OZ_TO_GRAM)

    if (this.hasGoldDwtPriceTarget) {
      this.goldDwtPriceTarget.textContent = USD_FORMATTER.format(goldOz / TROY_OZ_TO_DWT)
      this.silverDwtPriceTarget.textContent = USD_FORMATTER.format(silverOz / TROY_OZ_TO_DWT)
      this.platinumDwtPriceTarget.textContent = USD_FORMATTER.format(platinumOz / TROY_OZ_TO_DWT)
    }
  }

  // Main calculation
  calculate(event) {
    this.flashResults = !!event && [this.weightInputTarget, this.weightUnitTarget].includes(event.target)

    const goldOz = parseFloat(this.goldOzPriceTarget.value) || 0
    const silverOz = parseFloat(this.silverOzPriceTarget.value) || 0
    const platinumOz = parseFloat(this.platinumOzPriceTarget.value) || 0

    const weight = parseFloat(this.weightInputTarget.value) || 0
    const unit = this.weightUnitTarget.value

    // Convert weight to grams
    let weightInGrams = weight
    if (unit === "oz") {
      weightInGrams = weight * TROY_OZ_TO_GRAM
    } else if (unit === "kg") {
      weightInGrams = weight * 1000
    } else if (unit === "dwt") {
      weightInGrams = weight * 1.55517
    }

    // Gold karat calculations (14K highlighted as the most popular US jewelry karat)
    const goldPerGram = goldOz / TROY_OZ_TO_GRAM
    const goldResultsHtml = Object.entries(KARAT_PURITY).map(([karat, purity]) => {
      const pricePerGram = goldPerGram * purity
      const totalPrice = pricePerGram * weightInGrams
      return this.resultRow(karat, `${(purity * 100).toFixed(1)}%`, pricePerGram, totalPrice, weightInGrams, karat === "14K", unit, weight)
    }).join("")
    this.goldResultsTarget.innerHTML = goldResultsHtml

    // Silver calculation
    const silverPerGram = silverOz / TROY_OZ_TO_GRAM
    const silverTotal = silverPerGram * weightInGrams
    this.silverResultTarget.innerHTML =
      this.resultRow("Pure .999", "99.9%", silverPerGram, silverTotal, weightInGrams, false, unit, weight) +
      this.resultRow("Sterling .925", "92.5%", silverPerGram * 0.925, silverTotal * 0.925, weightInGrams, false, unit, weight)

    // Platinum calculation
    const platinumPerGram = platinumOz / TROY_OZ_TO_GRAM
    const platinumTotal = platinumPerGram * weightInGrams
    this.platinumResultTarget.innerHTML =
      this.resultRow("Pt950", "95.0%", platinumPerGram * 0.95, platinumTotal * 0.95, weightInGrams, false, unit, weight) +
      this.resultRow("Pt900", "90.0%", platinumPerGram * 0.90, platinumTotal * 0.90, weightInGrams, false, unit, weight)
  }

  // Render a single result row (card-style, no table)
  resultRow(label, purity, pricePerGram, total, weightInGrams, highlight = false, unit = "gram", inputWeight = 0) {
    const hasWeight = weightInGrams > 0
    const flashClass = this.flashResults ? " pmc-flash" : ""
    const totalClass = highlight ? "pmc-total pmc-total--lg" : "pmc-total small"
    const unitSymbols = { gram: "g", oz: "ozt", dwt: "dwt", kg: "kg" }
    const unitMultipliers = { gram: 1, oz: TROY_OZ_TO_GRAM, dwt: 1.55517, kg: 1000 }
    const unitSymbol = unitSymbols[unit] || "g"
    const formattedWeight = inputWeight.toLocaleString(undefined, { maximumFractionDigits: 4 })
    const context = inputWeight === 1 ? ` / ${unitSymbol}` : ` for ${formattedWeight} ${unitSymbol}`
    const unitPrice = pricePerGram * (unitMultipliers[unit] || 1)
    const priceDisplay = hasWeight
      ? `<span class="${totalClass}${flashClass}">${USD_FORMATTER.format(total)}</span><span class="pmc-total-context">${context}</span>`
      : `<span class="small text-muted${flashClass}">${USD_FORMATTER.format(unitPrice)}</span><span class="pmc-total-context"> / ${unitSymbol}</span>`
    const rowClass = highlight ? " pmc-result-row--highlight" : ""
    const badge = highlight ? `<span class="pmc-popular-badge">Most popular</span>` : ""
    return `
      <div class="pmc-result-row${rowClass} d-flex align-items-center justify-content-between py-2">
        <div>
          <span class="fw-medium small">${label}</span>
          <span class="text-muted" style="font-size: 0.7rem; margin-left: 4px;">${purity}</span>
          ${badge}
        </div>
        <div class="text-end">
          ${priceDisplay}
        </div>
      </div>
    `
  }

  // Refresh prices from API
  refresh() {
    this.fetchPrices()
  }

  updateStatus(status) {
    if (!this.hasStatusBadgeTarget) return

    const badge = this.statusBadgeTarget
    badge.classList.remove(
      "pmc-status--market",
      "pmc-status--stale",
      "pmc-status--custom",
      "pmc-status--estimated",
      "pmc-status--loading",
      "pmc-status--offline"
    )

    switch (status) {
      case "live":
        badge.classList.add("pmc-status--market")
        badge.textContent = "Market Prices"
        break
      case "custom":
        badge.classList.add("pmc-status--custom")
        badge.textContent = "Custom Prices"
        break
      case "stale":
        badge.classList.add("pmc-status--stale")
        badge.textContent = "Last Available Prices"
        break
      case "fallback":
        badge.classList.add("pmc-status--estimated")
        badge.textContent = "Estimated Prices"
        break
      case "loading":
        badge.classList.add("pmc-status--loading")
        badge.textContent = "Loading..."
        break
      case "offline":
        badge.classList.add("pmc-status--offline")
        badge.textContent = "Offline — Enter Manually"
        break
    }

    if (this.hasMarketTimestampTarget) {
      this.marketTimestampTarget.classList.toggle("d-none", !["live", "stale"].includes(status))
    }

  }
}
